import React, { useRef, useState, useEffect } from 'react'
import {
  IconButton,
  InputAdornment,
  TextField
} from '@mui/material'
import { Search, Clear } from '@mui/icons-material'
import { useStyles } from './style'

const SimpleSearch = props => {
  const classes = useStyles(props)

  const {
    onSearch,
    disabled,
    initialValue = '',
    className = '',
    debounceMs = 500,
    minChars = 3,
    ...textFieldProps
  } = props

  const inputRef = useRef()
  const debounceTimerRef = useRef()

  const [value, setValue] = useState(initialValue)
  const [invokedSearch, setInvokedSearch] = useState()
  const invokedSearchRef = useRef(invokedSearch)

  const execSearch = value => onSearch(value.trim())

  // Sync internal value with initialValue prop changes
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    invokedSearchRef.current = invokedSearch
  })

  useEffect(() => {
    if (!disabled && invokedSearchRef.current) {
      inputRef.current.focus()
      setInvokedSearch(false)
    }
  }, [disabled])

  // Auto-search with debouncing
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    const trimmedValue = value.trim()

    // If empty, trigger search immediately to clear results
    if (trimmedValue === '') {
      execSearch('')
      return
    }

    // Only search if minimum character count is met
    if (trimmedValue.length < minChars) {
      return
    }

    // Set up debounced search
    debounceTimerRef.current = setTimeout(() => {
      setInvokedSearch(true)
      execSearch(trimmedValue)
    }, debounceMs)

    // Cleanup on unmount or value change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [value, debounceMs, minChars]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setInvokedSearch(true)
    execSearch(value)
  }

  const handleChange = ({ target: { value } }) => setValue(value)

  const handleClear = () => {
    inputRef.current.focus()
    setValue('')
  }

  const handleKeyDown = ({ key }) => {
    if (document.activeElement === inputRef.current && key === 'Enter') {
      // Clear debounce timer and search immediately on Enter
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      handleSearch()
    }
  }

  return (
    <TextField
      variant='outlined'
      id='playlist-search-text'
      placeholder='Search playlists'
      disabled={disabled}
      onKeyDown={handleKeyDown}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start' className={classes.adornment}>
            <Search />
          </InputAdornment>
        ),
        endAdornment: value && (
          <InputAdornment position='end'>
            <IconButton
              onClick={handleClear}
              disabled={disabled}
              aria-label='Clear search text'
              data-test='playlist-search-term-clear'
              size='large'
            >
              <Clear />
            </IconButton>
          </InputAdornment>
        )
      }}
      inputProps={{
        'aria-label': 'Search playlists'
      }}
      value={value}
      onChange={handleChange}
      inputRef={inputRef}
      className={className}
      data-test='playlist-search-input'
      {...textFieldProps}
    />
  )
}

export default SimpleSearch
